import { localizedNumber } from "shared/numberParser";
import { getNumberFromNumberString, renderNumberValue } from "utils/string";
import { SUPERSCRIPT_STYLES } from "shared/rsDictionary";
import { defaultArrowAnnotationLeft, defaultGridColor, defaultHoverLabel, defaultSpikeConfig, defaultTickFont, } from "shared/plotly/defaultConfigs";
import { AXES_REGEXP, MainAxesName, } from "shared/plotly/types";
import { TimeUnits } from "shared/types";
import { formatPointCoord, getArrayMinMax, hexToRgba } from "utils/data";
import { getTimeUnit } from "utils/dateTime";
import { isNotEmpty } from "utils/validate";
import { ResultElementGraphViewType, ResultSetElementType, ResultElementSourceType, } from "store/rs-workspace/rs-workspace.types";
import { getSimulationPreviewXAxisPointValue } from "store/rs-workspace/utils/scenario.utils";
import { getSourceLineColor } from "store/rs-workspace/utils/results.utils";
import { getPointText } from "store/rs-workspace/utils/graph.utils";
import { getErrorsTrace, getTraceTemplate } from "shared/plotly/helpers";
import * as colors from "styles/colorValues.module.scss";
import * as fonts from "styles/fontValues.module.scss";
import * as dimensions from "styles/dimensionsValues.module.scss";
export const defaultAxisConfig = {
    tickfont: defaultTickFont,
    linewidth: 1,
    tickwidth: 1,
    gridcolor: defaultGridColor,
    linecolor: colors.s900,
};
export const defaultYAxisConfig = {
    ...defaultAxisConfig,
    showticklabels: true,
    tickformat: "000",
    tickangle: -55,
    linewidth: 2,
};
export const defaultEmptyData = { x: [null], y: [null], type: "scatter" };
const { decimal, group } = localizedNumber.numberSeparators;
export const getAxisRangesConfig = (rangesConfig, updatedRangeConfig) => {
    if (!rangesConfig && !updatedRangeConfig) {
        return {};
    }
    const { autoRange, fixedRange, min, max, showGrid } = {
        ...(rangesConfig ?? {}),
        ...(updatedRangeConfig ?? {}),
    };
    return {
        autorange: autoRange,
        ...(!autoRange && { range: [min, max] }),
        fixedrange: fixedRange,
        showgrid: showGrid ?? true,
    };
};
const DISCRETE_POINT_WIDTH = 0.04;
const getDiscretePointPosition = (resultIndex, x) => isNotEmpty(x)
    ? x + DISCRETE_POINT_WIDTH / 2 + resultIndex * DISCRETE_POINT_WIDTH
    : x;
const getDiscreteAxesMainShapePath = (x) => {
    if (isNotEmpty(x)) {
        const halfPointWidth = DISCRETE_POINT_WIDTH / 2;
        const numberX = x;
        return `M${numberX - halfPointWidth} -4H${numberX}V4H${numberX - halfPointWidth}Z M${numberX + halfPointWidth} -4H${numberX}V4H${numberX + halfPointWidth}Z`;
    }
    return "";
};
const getDiscreteAxesMainShapeConfig = (resultIndex, x, y, sourceColor) => ({
    yref: `y${resultIndex === 0 ? "" : resultIndex + 1}`,
    line: { width: 0 },
    name: "sla",
    fillcolor: sourceColor,
    layer: "above",
    type: "path",
    path: getDiscreteAxesMainShapePath(x),
    xanchor: x,
    ysizemode: "pixel",
    yanchor: y,
});
const getDiscreteAxesSpreadShapeConfig = (resultIndex, x, y0, y1, sourceColor) => ({
    yref: `y${resultIndex === 0 ? "" : resultIndex + 1}`,
    type: "rect",
    line: { width: 0 },
    fillcolor: hexToRgba(sourceColor, 0.1),
    layer: "above",
    x0: x - DISCRETE_POINT_WIDTH / 2,
    x1: x + DISCRETE_POINT_WIDTH / 2,
    y0,
    y1,
});
const getDiscreteAxesShapes = (graphResults, discreteXTicksMapping) => {
    return graphResults?.reduce((acc, { points, source }, resultIndex) => {
        const typeOfView = source?.viewType;
        const sourceColor = getSourceLineColor(source?.color, resultIndex);
        if (!source?.view) {
            return [...acc];
        }
        const pointsShapes = points?.reduce((acc, { point }) => {
            if (point.median === null) {
                return acc;
            }
            const x = getDiscretePointPosition(resultIndex, discreteXTicksMapping?.findIndex(({ idx }) => idx === point?.x));
            if (typeOfView === ResultElementGraphViewType.MeanWithSpread) {
                const mainShape = getDiscreteAxesMainShapeConfig(resultIndex, x, point?.average, sourceColor);
                const spreadShape = getDiscreteAxesSpreadShapeConfig(resultIndex, x, point?.upperSdSpread, point?.lowerSdSpread, sourceColor);
                return [...acc, spreadShape, mainShape];
            }
            const mainShape = getDiscreteAxesMainShapeConfig(resultIndex, x, point?.median, sourceColor);
            if (typeOfView === ResultElementGraphViewType.MedianWithSpread) {
                const spreadShape = getDiscreteAxesSpreadShapeConfig(resultIndex, x, point?.upperSpread, point?.lowerSpread, sourceColor);
                return [...acc, spreadShape, mainShape];
            }
            return [...acc, mainShape];
        }, []) ?? [];
        return [...acc, ...pointsShapes];
    }, []);
};
export const preparePlotLayout = (graphResults, xConfig, updatedRangesConfig, additionalAxisConfigs, isDiscreteGraph, discreteXTicksMapping) => {
    const visibleYAxes = graphResults?.filter(({ source }) => !!source?.view) ?? [];
    const moreThan3Axes = visibleYAxes.length > 3;
    const yAxes = (graphResults ?? []).reduce((acc, { source, sourceId }, index) => {
        const axisName = `yaxis${index === 0 ? "" : index + 1}`;
        const visibleAxisIdx = visibleYAxes.findIndex((visibleSource) => sourceId === visibleSource.sourceId);
        const color = getSourceLineColor(source?.color, index);
        const updatedRangeConfig = updatedRangesConfig?.sourceConfigs?.[sourceId];
        const rangeConfig = getAxisRangesConfig(source?.config, updatedRangeConfig);
        if (!visibleYAxes.length && index === 0) {
            return { ...acc, [axisName]: { ...defaultYAxisConfig, fixedrange: true } };
        }
        const axisConfig = {
            ...defaultYAxisConfig,
            linecolor: color,
            tickcolor: color,
            tickfont: { color: color },
            showline: source?.view ?? true,
            visible: source?.view ?? true,
            ...(index > 0 ? { overlaying: "y" } : {}),
            ...(visibleAxisIdx > (moreThan3Axes ? 2 : 1) ? { side: "right" } : {}),
            ...(additionalAxisConfigs?.[axisName] ?? {}),
            responseId: source.id,
            autorange: true,
            ...rangeConfig,
            axisName,
            showgrid: rangeConfig.showgrid ?? true,
            fixedrange: rangeConfig.fixedrange || !source.view,
        };
        return { ...acc, [axisName]: axisConfig };
    }, {}) ?? { yaxis: defaultYAxisConfig };
    return {
        dragmode: "pan",
        hovermode: "x unified",
        showlegend: false,
        autosize: true,
        hoverlabel: defaultHoverLabel,
        margin: {
            l: visibleYAxes.length
                ? (moreThan3Axes ? 3 : visibleYAxes.length === 3 ? 2 : visibleYAxes.length) * 55
                : 55,
            r: moreThan3Axes ? (visibleYAxes.length - 3) * 55 : 55,
            b: 37,
            t: 10,
        },
        separators: `${decimal}${group}`,
        xaxis: {
            ...defaultAxisConfig,
            ...defaultSpikeConfig,
            automargin: true,
            autorange: true,
            tickwidth: 1,
            tickcolor: colors.white,
            titlefont: {
                family: fonts.roboto,
                size: 14,
                color: colors.black,
            },
            axisName: MainAxesName.X_AXIS,
            ...(additionalAxisConfigs?.xaxis ?? {}),
            ...getAxisRangesConfig(xConfig, updatedRangesConfig?.xConfig),
            ...(!isDiscreteGraph
                ? { tickmode: "auto" }
                : {
                    tickmode: discreteXTicksMapping ? "array" : "auto",
                    tickvals: discreteXTicksMapping?.map((_, idx) => idx),
                    ticktext: discreteXTicksMapping?.map(({ name }) => name),
                }),
        },
        ...yAxes,
        annotations: [defaultArrowAnnotationLeft],
        ...(isDiscreteGraph ? { shapes: getDiscreteAxesShapes(graphResults, discreteXTicksMapping) } : {}),
    };
};
const getLinesGraphResultData = (acc, sourceResult, resultIndex, maxUnit, errorsSourceId, sourceForErrors, errorsTraceConfig, totalRunsCount) => {
    const { source, points, sourceLineTitle, sourceLineUnits, sourceLineComputedName, sourceLineTiming, elementType, sourceId, } = sourceResult;
    const typeOfView = source?.viewType;
    const sourceSourceType = source?.source?.type;
    const unit = !points?.length ? TimeUnits.hours : maxUnit;
    const isLineGraph = elementType === ResultSetElementType.LineGraph;
    const sourceColor = getSourceLineColor(source?.color, resultIndex);
    const medianTrace = getTraceTemplate(resultIndex, "lines", sourceColor, !!source.view);
    const averageTrace = getTraceTemplate(resultIndex, "lines", sourceColor, !!source.view);
    const medianSpreadTraces = [];
    let medianSpreadTraceTopHalf = { x: [], y: [] };
    let medianSpreadTraceBottomHalf = { x: [], y: [] };
    const averageSpreadTraces = [];
    let averageSpreadTraceTopHalf = { x: [], y: [] };
    let averageSpreadTraceBottomHalf = { x: [], y: [] };
    points?.forEach((pointData, index) => {
        const { point, errorCount } = { ...pointData };
        const text = getPointText(pointData, sourceLineTitle ?? "", sourceLineUnits, source.source?.type === ResultElementSourceType.Insight ? "" : sourceLineComputedName, sourceLineTiming, sourceSourceType, source.id === errorsSourceId, totalRunsCount);
        const failedRunsPercentage = Math.round((errorCount / (totalRunsCount ?? 1)) * 100);
        const x = isLineGraph ? point?.x : getSimulationPreviewXAxisPointValue(point?.x, unit);
        errorsTraceConfig.x.push(x);
        errorsTraceConfig.y.push(failedRunsPercentage);
        !isLineGraph && errorsTraceConfig.text.push(text);
        medianTrace.x.push(x);
        medianTrace.y.push(formatPointCoord(point?.median));
        medianTrace.text.push(text);
        medianTrace.customdata.push(sourceId);
        averageTrace.x.push(x);
        averageTrace.y.push(formatPointCoord(point?.average));
        averageTrace.text.push(text);
        medianTrace.customdata.push(sourceId);
        if (point.median === null) {
            return;
        }
        // fill medianSpreadTrace
        medianSpreadTraceTopHalf.x.push(x);
        medianSpreadTraceBottomHalf.x.push(x);
        medianSpreadTraceTopHalf.y.push(formatPointCoord(point?.upperSpread));
        medianSpreadTraceBottomHalf.y.push(formatPointCoord(point?.lowerSpread));
        // fill averageSpreadTrace
        averageSpreadTraceTopHalf.x.push(x);
        averageSpreadTraceBottomHalf.x.push(x);
        averageSpreadTraceTopHalf.y.push(formatPointCoord(point?.upperSdSpread));
        averageSpreadTraceBottomHalf.y.push(formatPointCoord(point?.lowerSdSpread));
        const nextPointIsEmpty = points[index + 1]?.point?.median === null;
        const reachedLastPoint = index === points?.length - 1;
        if (nextPointIsEmpty || reachedLastPoint) {
            medianSpreadTraces.push({
                ...getTraceTemplate(resultIndex, "lines+spread", sourceColor, !!source.view),
                x: [...medianSpreadTraceTopHalf.x, ...medianSpreadTraceBottomHalf.x.reverse()],
                y: [...medianSpreadTraceTopHalf.y, ...medianSpreadTraceBottomHalf.y.reverse()],
            });
            averageSpreadTraces.push({
                ...getTraceTemplate(resultIndex, "lines+spread", sourceColor, !!source.view),
                x: [...averageSpreadTraceTopHalf.x, ...averageSpreadTraceBottomHalf.x.reverse()],
                y: [...averageSpreadTraceTopHalf.y, ...averageSpreadTraceBottomHalf.y.reverse()],
            });
            medianSpreadTraceTopHalf = { x: [], y: [] };
            medianSpreadTraceBottomHalf = { x: [], y: [] };
            averageSpreadTraceTopHalf = { x: [], y: [] };
            averageSpreadTraceBottomHalf = { x: [], y: [] };
        }
    });
    const base = [...acc];
    if (sourceForErrors) {
        base.unshift(errorsTraceConfig);
    }
    switch (typeOfView) {
        case ResultElementGraphViewType.MedianWithSpread:
            return [...base, medianTrace, ...medianSpreadTraces];
        case ResultElementGraphViewType.MeanWithSpread:
            return [...base, averageTrace, ...averageSpreadTraces];
        default:
            return [...base, medianTrace];
    }
};
const getDiscreteGraphResultData = (acc, sourceResult, resultIndex, errorsSourceId, sourceForErrors, errorsTraceConfig, totalRunsCount, alternativeFlowsMapping) => {
    const { source, points, sourceLineTitle, sourceLineUnits, sourceLineComputedName, sourceLineTiming, sourceId, } = sourceResult;
    const typeOfView = source?.viewType;
    const sourceSourceType = source?.source?.type;
    const trace = getTraceTemplate(resultIndex, "discrete", getSourceLineColor(source?.color, resultIndex), !!source.view);
    points?.forEach((pointData) => {
        const { point, errorCount } = { ...pointData };
        const x = getDiscretePointPosition(resultIndex, alternativeFlowsMapping?.findIndex(({ idx }) => idx === point?.x));
        const failedRunsPercentage = Math.round((errorCount / (totalRunsCount ?? 1)) * 100);
        errorsTraceConfig.x.push(x);
        errorsTraceConfig.y.push(failedRunsPercentage);
        trace.x.push(x);
        if (point.median === null) {
            trace.y.push(null);
            trace.text.push(null);
            trace.customdata.push(null);
        }
        else {
            const text = getPointText(pointData, sourceLineTitle ?? "", sourceLineUnits, source.source?.type === ResultElementSourceType.Insight ? "" : sourceLineComputedName, sourceLineTiming, sourceSourceType, source.id === errorsSourceId, totalRunsCount);
            trace.text.push(text);
            trace.customdata.push(sourceId);
            if (typeOfView === ResultElementGraphViewType.MeanWithSpread) {
                trace.y.push(formatPointCoord(point?.average));
            }
            else {
                trace.y.push(formatPointCoord(point?.median));
            }
        }
    });
    if (sourceForErrors) {
        acc.unshift(errorsTraceConfig);
    }
    return [...acc, trace];
};
export const prepareLineGraphData = (graphResults, errorsSourceId, totalRunsCount, alternativeFlowsMapping) => {
    if (!graphResults?.length) {
        return [defaultEmptyData];
    }
    const maxTimestamps = graphResults?.map(({ points }) => points?.[points.length - 1]?.point?.x ?? 0);
    const maxUnit = getTimeUnit(getArrayMinMax(maxTimestamps)?.max);
    const sourceForErrors = graphResults?.find(({ sourceId }) => sourceId === errorsSourceId);
    return graphResults.reduce((acc, sourceResult, resultIndex) => {
        const { elementType } = sourceResult;
        const errorsTraceConfig = getErrorsTrace(elementType === ResultSetElementType.LineGraph);
        if (alternativeFlowsMapping) {
            return getDiscreteGraphResultData(acc, sourceResult, resultIndex, errorsSourceId, sourceForErrors, errorsTraceConfig, totalRunsCount, alternativeFlowsMapping);
        }
        return getLinesGraphResultData(acc, sourceResult, resultIndex, maxUnit, errorsSourceId, sourceForErrors, errorsTraceConfig, totalRunsCount);
    }, []);
};
export const handlePlotHover = (plotRef, getTitleInnerHtmlCallback, ev, checkActionMenuAvailable) => {
    const hoverlayer = plotRef?.current?.el?.querySelector(".hoverlayer");
    const mainSVG = plotRef?.current?.el?.querySelector(".main-svg");
    const plotLegend = hoverlayer?.querySelector(".legend");
    const clonedLegendToRemove = hoverlayer?.querySelector(".legend.cloned");
    const actionButtonToRemove = hoverlayer?.querySelector(".actionButton.legend.cloned");
    if (hoverlayer && plotLegend) {
        clonedLegendToRemove && hoverlayer.removeChild(clonedLegendToRemove);
        actionButtonToRemove && hoverlayer.removeChild(actionButtonToRemove);
        const clonedLegend = plotLegend.cloneNode(true);
        const actionsButton = plotLegend.cloneNode(true);
        const spike = hoverlayer?.querySelectorAll(".spikeline.crisp")[1];
        const scrollBox = clonedLegend?.querySelector(".scrollbox");
        const scrollBoxGroups = scrollBox?.querySelector(".groups");
        const legendTitle = scrollBox?.querySelector(".legendtitletext");
        if (spike && scrollBox && scrollBoxGroups && legendTitle) {
            const titleText = getTitleInnerHtmlCallback(legendTitle.innerHTML);
            legendTitle.innerHTML = `<tspan x="1" font-size=14px style="font-weight: bold;">${titleText}</tspan>`;
            legendTitle.style.transform = "translate(10px, 5px)";
            scrollBox.style.transform = "translate(-5px, -8px)";
            scrollBoxGroups.style.transform = "translateY(10px)";
            const traces = scrollBoxGroups.querySelectorAll(".traces");
            traces.forEach((el, index) => {
                const legendText = el.querySelector(".legendtext");
                const layers = el.querySelector(".layers");
                if (legendText) {
                    legendText.style.fontSize = "12px";
                }
                const lines = legendText?.querySelectorAll(".line");
                lines?.forEach((line) => {
                    const tspans = line?.querySelectorAll("tspan");
                    const values = line?.querySelectorAll("a");
                    tspans?.forEach((tspan) => {
                        if (!tspan) {
                            return;
                        }
                        if (tspan.style.top === SUPERSCRIPT_STYLES.top) {
                            tspan.style.baselineShift = "super";
                        }
                        if (tspan.style.verticalAlign) {
                            // though those are not exactly the same, it is used to avoid hardcoded align values
                            tspan.style.baselineShift = tspan.style.verticalAlign;
                        }
                    });
                    values?.forEach((value) => {
                        if (!value) {
                            return;
                        }
                        value.innerHTML = renderNumberValue(getNumberFromNumberString(value.innerHTML));
                        value.setAttribute("style", "pointer-events: none; fill: inherit");
                    });
                });
                if (layers) {
                    const pointData = ev?.points?.sort((a, b) => a.curveNumber - b.curveNumber)?.[index]?.data;
                    const lineFill = pointData?.line?.color ?? pointData?.marker?.color;
                    const dash = pointData?.line?.dash;
                    layers.innerHTML = `<line x1="8" x2="28" y1="-5" y2="-5" stroke="${lineFill}" stroke-width="3" ${dash ? `stroke-dasharray="3.5, 2"` : ""}/>`;
                }
            });
            hoverlayer.appendChild(clonedLegend);
            if (checkActionMenuAvailable && checkActionMenuAvailable(ev?.points)) {
                const horizontalLine = `<rect width="24px" height="1px" fill="#fff" transform="translate(8, 20)" />`;
                const verticalLine = `<rect width="1px" height="24px" fill="#fff" transform="translate(20, 8)" />`;
                actionsButton.innerHTML = `<rect width="40px" height="40px" fill="#000" />${horizontalLine}${verticalLine}`;
                hoverlayer.appendChild(actionsButton);
            }
            scrollBox.removeAttribute("clip-path");
            const boundingBox = scrollBox.getBBox();
            const bg = clonedLegend.querySelector(".bg");
            if (boundingBox) {
                if (bg) {
                    bg.setAttribute("width", `${boundingBox.width}px`);
                    bg.setAttribute("height", `${boundingBox.height}px`);
                }
                const mainSVGWidth = mainSVG?.getAttribute("width");
                const spikePosition = spike.getAttribute("x1") || spike.getAttribute("x2") || "";
                const legendTransform = plotLegend.getAttribute("transform") || "";
                const [, yPosition] = legendTransform
                    .substring(legendTransform.indexOf("(") + 1, legendTransform.indexOf(")"))
                    .split(",");
                const transform = { x: 0, y: yPosition };
                if (Number(mainSVGWidth) < Number(spikePosition) + boundingBox.width) {
                    const plotElementBBox = plotRef.current?.el?.getBoundingClientRect();
                    const leftPosition = Number(spikePosition) - boundingBox.width - 7;
                    const rootLeft = Number(dimensions["rs-left-panel-width"]?.replace("px", "") ?? 0);
                    if (rootLeft > leftPosition + (plotElementBBox?.x ?? 0)) {
                        transform.x = Number(spikePosition) + 7;
                    }
                    else {
                        transform.x = leftPosition;
                    }
                }
                else {
                    transform.x = Number(spikePosition) + 7;
                }
                clonedLegend.setAttribute("style", `transform: translate(${transform.x}px, ${transform.y}px)`);
                actionsButton.setAttribute("style", `transform: translate(${Number(spikePosition) - 20}px, ${10}px);`);
            }
            clonedLegend.classList.add("visible", "cloned");
            actionsButton.classList.add("actionButton", "visible", "cloned");
        }
    }
    else {
        clonedLegendToRemove && hoverlayer?.removeChild(clonedLegendToRemove);
        actionButtonToRemove && hoverlayer?.removeChild(actionButtonToRemove);
    }
};
export const prepareRangeConfigsOnLockButtonClick = (plotLayout, updatedRangeConfigsRef, locked, axisName) => Object.keys(plotLayout)
    .filter((key) => AXES_REGEXP.test(key))
    .reduce((acc, key) => {
    const axisConfig = plotLayout[key];
    const updatedAxisConfig = key === MainAxesName.X_AXIS
        ? updatedRangeConfigsRef.current?.xConfig
        : updatedRangeConfigsRef.current?.sourceConfigs?.[axisConfig?.responseId ?? -1];
    const isTargetAxis = axisName === axisConfig?.axisName;
    return {
        ...acc,
        [key]: {
            responseId: axisConfig?.responseId,
            autorange: updatedAxisConfig?.autoRange ?? axisConfig?.autorange,
            min: updatedAxisConfig?.min ?? axisConfig?.range?.[0],
            max: updatedAxisConfig?.max ?? axisConfig?.range?.[1],
            fixedrange: isTargetAxis ? locked : (updatedAxisConfig?.fixedRange ?? axisConfig?.fixedrange),
            showgrid: updatedAxisConfig?.showGrid ?? axisConfig?.showgrid,
        },
    };
}, {});
